import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { RefreshCw, Search, Activity, Cpu, HardDrive, MemoryStick } from 'lucide-react';
import { monitoringApi } from '../../api/endpoints/monitoring';
import { useDebounce } from '../../hooks/useDebounce';
import { useSocket } from '../../hooks/useSocket';
import { queryClient } from '../../providers/QueryProvider';
import { StatCard } from '../../components/ui/Card';
import { Pagination } from '../../components/ui/Pagination';
import { MetricBar } from '../../components/ui/MetricBar';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatTimeAgo, formatPercent } from '../../utils/formatters';
import type { Client } from '../../types/client';

function computeHealth(c: Client): string {
  if (!c.metrics) return 'no-data';
  const age = c.metrics.collected_at ? (Date.now() - new Date(c.metrics.collected_at).getTime()) / 1000 : 999;
  if (age > 120) return 'stale';
  if (c.metrics.tunnel_status === 'healthy' && c.metrics.ssh_connection === 'up') return 'healthy';
  if (c.metrics.tunnel_status === 'degraded') return 'degraded';
  if (c.metrics.ssh_connection === 'down' || c.metrics.tunnel_status === 'down') return 'down';
  return 'degraded';
}

const healthColors: Record<string, string> = {
  healthy: 'bg-green-500',
  degraded: 'bg-yellow-500',
  down: 'bg-red-500',
  stale: 'bg-gray-400',
  'no-data': 'bg-gray-300',
};

const healthLabels: Record<string, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  down: 'Down',
  stale: 'Stale',
  'no-data': 'No Data',
};

export function MonitoringPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState('');
  const [healthFilter, setHealthFilter] = useState('');

  const debouncedSearch = useDebounce(search);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['monitoring', { page, limit, search: debouncedSearch, health: healthFilter }],
    queryFn: () => monitoringApi.overview({ page, limit, search: debouncedSearch, health: healthFilter }),
    refetchInterval: 30_000,
  });

  // Real-time metrics updates
  useSocket('metricsUpdate', useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['monitoring'] });
  }, []));

  const clients = data?.clients || [];
  const summary = data?.summary || { total: 0, healthy: 0, degraded: 0, down: 0, noMetrics: 0, avgCpu: 0, avgMemory: 0, avgDisk: 0 };

  // Client-side health filtering for the grid display
  const filteredClients = healthFilter
    ? clients.filter(c => computeHealth(c) === healthFilter)
    : clients;

  const totalPages = Math.max(1, Math.ceil(summary.total / limit));

  if (isLoading && !data) return <PageSpinner />;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Monitoring</h1>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">Real-time health monitoring for all clients</p>
        </div>
        <button onClick={() => refetch()} className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 transition-colors flex items-center gap-1 text-xs">
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
        <div className="cursor-pointer" onClick={() => { setHealthFilter(''); setPage(1); }}>
          <StatCard label="Total" value={summary.total} color="gray" icon={<Activity size={18} />} />
        </div>
        <div className="cursor-pointer" onClick={() => { setHealthFilter('healthy'); setPage(1); }}>
          <StatCard label="Healthy" value={summary.healthy} color="green" />
        </div>
        <div className="cursor-pointer" onClick={() => { setHealthFilter('degraded'); setPage(1); }}>
          <StatCard label="Degraded" value={summary.degraded} color="yellow" />
        </div>
        <div className="cursor-pointer" onClick={() => { setHealthFilter('down'); setPage(1); }}>
          <StatCard label="Down" value={summary.down} color="red" />
        </div>
        <StatCard label="Avg CPU" value={formatPercent(summary.avgCpu)} color="blue" icon={<Cpu size={18} />} />
        <StatCard label="Avg Memory" value={formatPercent(summary.avgMemory)} color="purple" icon={<MemoryStick size={18} />} />
        <StatCard label="Avg Disk" value={formatPercent(summary.avgDisk)} color="teal" icon={<HardDrive size={18} />} />
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-[#111] rounded-md shadow-sm border border-gray-200 dark:border-gray-800 p-3 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[256px] relative">
            <Search size={16} className="absolute left-2.5 top-2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by name, hostname, IP, deployment..."
              className="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
            />
          </div>
          <select
            value={healthFilter}
            onChange={e => { setHealthFilter(e.target.value); setPage(1); }}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white"
          >
            <option value="">All Health</option>
            <option value="healthy">Healthy</option>
            <option value="degraded">Degraded</option>
            <option value="down">Down</option>
            <option value="stale">Stale</option>
            <option value="no-data">No Data</option>
          </select>
          <select
            value={limit}
            onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white"
          >
            <option value={25}>25/page</option>
            <option value={50}>50/page</option>
            <option value={100}>100/page</option>
            <option value={200}>200/page</option>
          </select>
        </div>
      </div>

      {/* Client Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filteredClients.map(client => {
          const health = computeHealth(client);
          const m = client.metrics;
          return (
            <div
              key={client._id}
              className="bg-white dark:bg-[#111] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-3 hover:shadow-md hover:border-purple-300 dark:hover:border-purple-600 transition-all cursor-pointer"
              onClick={() => navigate(`/clients/${client.client_id}`)}
            >
              {/* Card Header: Status + Name */}
              <div className="flex items-start gap-2 mb-3">
                <span
                  className={`mt-1 inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${healthColors[health]} ${health === 'healthy' ? 'animate-pulse' : ''}`}
                  title={healthLabels[health]}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {client.client_name || client.client_id}
                  </p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">
                    {client.hostname || client.client_id}
                  </p>
                </div>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                  health === 'healthy' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : health === 'degraded' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                  : health === 'down' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {healthLabels[health]}
                </span>
              </div>

              {/* Metrics */}
              {m ? (
                <div className="space-y-1.5 mb-3">
                  <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400">
                    <span>Latency</span>
                    <span className="font-medium">{m.latency_ms != null ? `${Math.round(m.latency_ms)}ms` : '-'}</span>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">
                      <span>CPU</span>
                      <span className="font-medium">{formatPercent(m.cpu_percent)}</span>
                    </div>
                    <MetricBar value={m.cpu_percent} color="blue" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">
                      <span>Memory</span>
                      <span className="font-medium">{formatPercent(m.memory_percent)}</span>
                    </div>
                    <MetricBar value={m.memory_percent} color="indigo" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">
                      <span>Disk</span>
                      <span className="font-medium">{formatPercent(m.disk_percent)}</span>
                    </div>
                    <MetricBar value={m.disk_percent} color="purple" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-4 mb-3">
                  <span className="text-xs text-gray-400 dark:text-gray-500">No metrics available</span>
                </div>
              )}

              {/* Footer: SSH Status + Last Seen */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    m?.ssh_connection === 'up' ? 'bg-green-500' : 'bg-red-400'
                  }`} />
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    SSH {m?.ssh_connection === 'up' ? 'Up' : 'Down'}
                  </span>
                </div>
                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                  {formatTimeAgo(client.last_seen)}
                </span>
              </div>

              {/* View Details link */}
              <div className="mt-2 text-center">
                <Link
                  to={`/clients/${client.client_id}`}
                  className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 text-xs font-medium"
                  onClick={e => e.stopPropagation()}
                >
                  View Details
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredClients.length === 0 && !isLoading && (
        <div className="bg-white dark:bg-[#111] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-8 text-center">
          <Activity size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No clients found matching your filters</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
